import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String }

  connect() {
    this.showLoadingWarning()
    this.loadTotals()
  }

  showLoadingWarning() {
    // Create warning element with fixed positioning to avoid layout shifts
    const warning = document.createElement('div')
    warning.id = 'totals-loading-warning'
    warning.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 1050;
      padding: 12px 20px;
      background-color: #fff3cd;
      border: 1px solid #ffc107;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 14px;
      color: #856404;
      transition: opacity 0.3s ease, transform 0.3s ease;
    `
    warning.innerHTML = `
      <i class="fas fa-spinner fa-spin"></i>
      <span><strong>Loading totals...</strong></span>
    `
    document.body.appendChild(warning)
  }

  hideLoadingWarning() {
    const warning = document.getElementById('totals-loading-warning')
    if (warning) {
      warning.style.opacity = '0'
      warning.style.transform = 'translateY(10px)'
      setTimeout(() => warning.remove(), 300)
    }
  }

  async loadTotals() {
    const url = this.urlValue
    if (!url) {
      console.error('report-totals: No URL provided')
      this.hideLoadingWarning()
      this.showError()
      return
    }

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Failed to load totals')

      const totals = await response.json()
      this.updateTotals(totals)
      this.hideLoadingWarning()
    } catch (error) {
      console.error('Error loading totals:', error)
      this.showError()
      this.hideLoadingWarning()
    }
  }

  updateTotals(totals) {
    // Update count
    const countCell = this.element.querySelector('[data-total="count"]')
    if (countCell) countCell.textContent = totals.count

    // Integer fields (not currency)
    const integerFields = [
      'quantity', 'received_quantity', 'total_pq', 'pq', 'incoming',
      'qty', 'sku_qty', 'total_units', 'inventory_qty', 'invoices_count',
      'invoice_line_items_count'
    ]
    integerFields.forEach(field => {
      const cell = this.element.querySelector(`[data-total="${field}"]`)
      if (cell && totals[field] !== undefined) {
        cell.textContent = totals[field].toLocaleString()
      }
    })

    // Currency totals
    const currencyFields = [
      'subtotal', 'shipping', 'tax', 'total', 'profit',
      'total_paid', 'owing', 'sales', 'cost', 'total_price',
      'total_cost', 'unit_sell', 'total_sell', 'total_line_items',
      'total_fulfilled', 'total_returns',
      'restocking_fee', 'shipping_refund', 'return_total',
      'supplier_cost', 'total_tax', 'total_shipping', 'total_items',
      'total_amount_received', 'total_amount_paid', 'total_pa', 'pa',
      'unit_cost', 'initial_balance', 'balance', 'amount',
      'opening_balance', 'closing_balance', 'inventory_value',
      'ordered', 'paid', 'net_balance', 'unapplied_credits', 'unpaid_invoices',
      'current', 'one_thirty', 'thirty_sixty', 'sixty_ninty', 'ninty_one_twenty',
      'more_than_one_twenty'
    ]
    currencyFields.forEach(field => {
      const cell = this.element.querySelector(`[data-total="${field}"]`)
      if (cell && totals[field] !== undefined) {
        cell.textContent = this.formatCurrency(totals[field])
      }
    })
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  showError() {
    // Find all cells with data-total attribute that show "Loading..." and replace with error indicator
    const cells = this.element.querySelectorAll('[data-total]')
    cells.forEach(cell => {
      if (cell.textContent.trim() === 'Loading...') {
        cell.innerHTML = '<span class="text-danger" title="Failed to load">—</span>'
      }
    })
  }
}
